import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import { CartProvider } from './context/CartContext.jsx';
import { ToastProvider } from './context/ToastContext.jsx';
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import DartSvgDefs from './components/layout/DartSvgDefs.jsx';
import PrivateRoute from './components/routing/PrivateRoute.jsx';
import AdminRoute from './components/routing/AdminRoute.jsx';
import HomePage from './pages/HomePage.jsx';
import NewsPage from './pages/NewsPage.jsx';
import ShopPage from './pages/ShopPage.jsx';
import ProductDetailPage from './pages/ProductDetailPage.jsx';
import CartPage from './pages/CartPage.jsx';
import CheckoutPage from './pages/CheckoutPage.jsx';
import SuccessPage from './pages/SuccessPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import VerifyEmailPage from './pages/VerifyEmailPage.jsx';
import ResetPasswordPage from './pages/ResetPasswordPage.jsx';
import AccountPage from './pages/AccountPage.jsx';
import KnowledgePage from './pages/KnowledgePage.jsx';
import AboutPage from './pages/AboutPage.jsx';
import VirtualPage from './pages/VirtualPage.jsx';
import VirtualDetailPage from './pages/VirtualDetailPage.jsx';
import SharedVirtualPage from './pages/SharedVirtualPage.jsx';
import RegisterPage from './pages/RegisterPage.jsx';
import GalleryPage from './pages/GalleryPage.jsx';
import DartCardPage from './pages/DartCardPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import NewsArticlePage from './pages/NewsArticlePage.jsx';
import PlayerProfilePage from './pages/PlayerProfilePage.jsx';
import AdminLayout from './pages/admin/AdminLayout.jsx';
import AdminIndex from './components/routing/AdminIndex.jsx';
import AdminProductsPage from './pages/admin/AdminProductsPage.jsx';
import AdminProductForm from './pages/admin/AdminProductForm.jsx';
import AdminOrdersPage from './pages/admin/AdminOrdersPage.jsx';
import AdminSettingsPage from './pages/admin/AdminSettingsPage.jsx';
import AdminRolesPage from './pages/admin/AdminRolesPage.jsx';
import AdminUsersPage from './pages/admin/AdminUsersPage.jsx';
import AdminMembershipLevelsPage from './pages/admin/AdminMembershipLevelsPage.jsx';
import AdminInquiriesPage from './pages/admin/AdminInquiriesPage.jsx';
import AdminThemesPage from './pages/admin/AdminThemesPage.jsx';
import AdminVirtualPage from './pages/admin/AdminVirtualPage.jsx';
import OrderDetailPage from './pages/OrderDetailPage.jsx';
import WriteReviewPage from './pages/WriteReviewPage.jsx';
import ReturnRefundPage from './pages/ReturnRefundPage.jsx';
import ActivationCodesPage from './pages/ActivationCodesPage.jsx';
import DevNav from './components/dev/DevNav.jsx';
import ScrollToTop from './components/routing/ScrollToTop.jsx';
import { AnalyticsConsentBanner, AnalyticsPageViews } from './components/analytics/AnalyticsTracker.jsx';

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <CartProvider>
          <Router>
            <ScrollToTop />
            <AnalyticsPageViews />
            <DartSvgDefs />
            <Routes>
              {/* 後台：完全獨立的 Layout，不顯示前台 Header/Footer */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminIndex />} />
                <Route path="products" element={<AdminProductsPage />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/:id/edit" element={<AdminProductForm />} />
                <Route path="themes" element={<AdminThemesPage />} />
                <Route path="virtual" element={<AdminVirtualPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="roles" element={<AdminRolesPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="membership-levels" element={<AdminMembershipLevelsPage />} />
                <Route path="inquiries" element={<AdminInquiriesPage />} />
              </Route>

              {/* 前台：共用 Header / Footer 包裝 */}
              <Route path="*" element={<PublicLayout />} />
            </Routes>
            <AnalyticsConsentBanner />
          </Router>
        </CartProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

/**
 * 前台 Layout：Header + Routes + Footer。
 * 抽出此元件讓後台可以完全跳過前台外殼。
 */
function PublicLayout() {
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/virtual" element={<PrivateRoute><VirtualPage /></PrivateRoute>} />
        <Route path="/virtual/:id" element={<PrivateRoute><VirtualDetailPage /></PrivateRoute>} />
        {/* 虛擬商品為會員制：瀏覽與登記皆需登入 */}
        {/* 分享連結：免登入，用 token 檢視單一虛擬商品 */}
        <Route path="/v/:token" element={<SharedVirtualPage />} />
        <Route path="/register" element={<PrivateRoute><RegisterPage /></PrivateRoute>} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/gallery/:id" element={<GalleryPage />} />
        <Route path="/dart-card" element={<DartCardPage />} />
        <Route path="/news/:id" element={<NewsArticlePage />} />
        <Route path="/player/:slug" element={<PlayerProfilePage />} />

        <Route path="/checkout" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
        <Route path="/success"  element={<PrivateRoute><SuccessPage /></PrivateRoute>} />
        <Route path="/account"  element={<PrivateRoute><AccountPage /></PrivateRoute>} />
        <Route path="/account/orders/:id"        element={<PrivateRoute><OrderDetailPage /></PrivateRoute>} />
        <Route path="/account/orders/:id/review" element={<PrivateRoute><WriteReviewPage /></PrivateRoute>} />
        <Route path="/account/orders/:id/return" element={<PrivateRoute><ReturnRefundPage /></PrivateRoute>} />
        <Route path="/account/activation-codes"  element={<PrivateRoute><ActivationCodesPage /></PrivateRoute>} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      {process.env.NODE_ENV === 'development' && <DevNav />}
    </>
  );
}

export default App;
